import React, { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { 
  Key, 
  Activity, 
  Shield, 
  Zap, 
  Copy, 
  Eye, 
  EyeOff,
  BarChart3,
  AlertTriangle,
  CheckCircle,
  Plus,
  Settings,
  DollarSign,
  Clock,
  TrendingUp
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";

interface APIKey {
  id: string;
  name: string;
  key: string;
  status: 'active' | 'inactive' | 'expired';
  tier: 'free' | 'premium' | 'enterprise';
  requestsUsed: number;
  requestsLimit: number;
  rateLimit: string;
  lastUsed?: string;
  createdAt: string;
  expiresAt?: string;
  scopes: string[];
}

interface UsageMetrics {
  totalRequests: number;
  successfulRequests: number;
  errorRequests: number;
  averageResponseTime: number;
  topEndpoints: Array<{ endpoint: string; requests: number }>;
  hourlyUsage: Array<{ hour: string; requests: number }>;
}

export const AdvancedAPIManagement: React.FC = () => {
  const { toast } = useToast();
  const [showKey, setShowKey] = useState<Record<string, boolean>>({});
  const [newKeyName, setNewKeyName] = useState("");

  const [apiKeys] = useState<APIKey[]>([
    {
      id: '1',
      name: 'Production API Key',
      key: 'api_prod_abc123def456ghi789',
      status: 'active',
      tier: 'enterprise',
      requestsUsed: 245680,
      requestsLimit: 1000000,
      rateLimit: '10,000/hour',
      lastUsed: '2024-01-20T14:30:00Z',
      createdAt: '2024-01-01T00:00:00Z',
      scopes: ['read:bills', 'write:bills', 'read:analytics', 'write:webhooks']
    },
    {
      id: '2',
      name: 'Development API Key',
      key: 'api_dev_xyz789uvw456rst123',
      status: 'active',
      tier: 'premium',
      requestsUsed: 15420,
      requestsLimit: 100000,
      rateLimit: '1,000/hour',
      lastUsed: '2024-01-19T09:15:00Z',
      createdAt: '2024-01-10T00:00:00Z',
      scopes: ['read:bills', 'read:analytics']
    },
    {
      id: '3',
      name: 'Legacy Integration',
      key: 'api_legacy_old789xyz456abc123',
      status: 'inactive',
      tier: 'free',
      requestsUsed: 98750,
      requestsLimit: 50000,
      rateLimit: '500/hour',
      lastUsed: '2024-01-15T16:45:00Z',
      createdAt: '2023-12-01T00:00:00Z',
      scopes: ['read:bills']
    }
  ]);

  const [usageMetrics] = useState<UsageMetrics>({
    totalRequests: 261100,
    successfulRequests: 259876,
    errorRequests: 1224,
    averageResponseTime: 145,
    topEndpoints: [
      { endpoint: '/api/v1/bills', requests: 125400 },
      { endpoint: '/api/v1/analytics', requests: 89320 },
      { endpoint: '/api/v1/suppliers', requests: 46380 }
    ],
    hourlyUsage: Array.from({ length: 24 }, (_, i) => ({
      hour: `${i}:00`,
      requests: Math.floor(Math.random() * 1000) + 500
    }))
  });

  const toggleKeyVisibility = (keyId: string) => {
    setShowKey(prev => ({ ...prev, [keyId]: !prev[keyId] }));
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    toast({
      title: "Copied",
      description: "API key copied to clipboard",
    });
  };

  const getTierColor = (tier: string) => {
    switch (tier) {
      case 'enterprise': return 'default';
      case 'premium': return 'secondary';
      case 'free': return 'outline';
      default: return 'outline';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'default';
      case 'inactive': return 'secondary';
      case 'expired': return 'destructive';
      default: return 'outline';
    }
  };

  const maskKey = (key: string) => {
    return key.substring(0, 12) + '...' + key.substring(key.length - 8);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">API Management</h1>
          <p className="text-muted-foreground">
            Manage API keys, monitor usage, and configure access controls
          </p>
        </div>
        <Button>
          <Plus className="h-4 w-4 mr-2" />
          Generate New Key
        </Button>
      </div>

      {/* Usage Overview */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Total Requests
            </CardTitle>
            <Activity className="h-4 w-4 text-primary" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{usageMetrics.totalRequests.toLocaleString()}</div>
            <p className="text-xs text-success">+12% from last month</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Success Rate
            </CardTitle>
            <CheckCircle className="h-4 w-4 text-success" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {((usageMetrics.successfulRequests / usageMetrics.totalRequests) * 100).toFixed(1)}%
            </div>
            <p className="text-xs text-muted-foreground">
              {usageMetrics.errorRequests} errors
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Avg Response Time
            </CardTitle>
            <Clock className="h-4 w-4 text-primary" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{usageMetrics.averageResponseTime}ms</div>
            <p className="text-xs text-success">-8ms from last month</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Revenue Impact
            </CardTitle>
            <DollarSign className="h-4 w-4 text-primary" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">$28.4K</div>
            <p className="text-xs text-success">+23% from API usage</p>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="keys" className="space-y-6">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="keys">API Keys</TabsTrigger>
          <TabsTrigger value="usage">Usage Analytics</TabsTrigger>
          <TabsTrigger value="billing">Usage-Based Billing</TabsTrigger>
          <TabsTrigger value="security">Security & Rate Limits</TabsTrigger>
        </TabsList>

        <TabsContent value="keys" className="space-y-6">
          {/* Generate New Key */}
          <Card>
            <CardHeader>
              <CardTitle>Generate New API Key</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex gap-4">
                <div className="flex-1">
                  <Label htmlFor="keyName">Key Name</Label>
                  <Input
                    id="keyName"
                    placeholder="Production Integration Key"
                    value={newKeyName}
                    onChange={(e) => setNewKeyName(e.target.value)}
                  />
                </div>
                <div className="flex items-end">
                  <Button disabled={!newKeyName}>
                    <Key className="h-4 w-4 mr-2" />
                    Generate Key
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* API Keys List */}
          <div className="space-y-4">
            {apiKeys.map((apiKey) => (
              <Card key={apiKey.id}>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="text-lg">{apiKey.name}</CardTitle>
                      <div className="flex items-center gap-2 mt-2">
                        <Badge variant={getTierColor(apiKey.tier)}>
                          {apiKey.tier.toUpperCase()}
                        </Badge>
                        <Badge variant={getStatusColor(apiKey.status)}>
                          {apiKey.status}
                        </Badge>
                        <span className="text-sm text-muted-foreground">
                          {apiKey.rateLimit}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => toggleKeyVisibility(apiKey.id)}
                      >
                        {showKey[apiKey.id] ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => copyToClipboard(apiKey.key)}
                      >
                        <Copy className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="sm">
                        <Settings className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="font-mono text-sm p-3 bg-muted rounded border">
                      {showKey[apiKey.id] ? apiKey.key : maskKey(apiKey.key)}
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <div className="text-sm font-medium">Usage This Month</div>
                        <div className="text-2xl font-bold">
                          {apiKey.requestsUsed.toLocaleString()}
                        </div>
                        <div className="text-sm text-muted-foreground">
                          of {apiKey.requestsLimit.toLocaleString()} limit
                        </div>
                        <div className="w-full bg-muted rounded-full h-2 mt-2">
                          <div 
                            className="bg-primary h-2 rounded-full" 
                            style={{ width: `${(apiKey.requestsUsed / apiKey.requestsLimit) * 100}%` }}
                          />
                        </div>
                      </div>

                      <div>
                        <div className="text-sm font-medium">Last Used</div>
                        <div className="text-lg">
                          {apiKey.lastUsed ? 
                            new Date(apiKey.lastUsed).toLocaleDateString() : 
                            'Never'
                          }
                        </div>
                        <div className="text-sm text-muted-foreground">
                          Created: {new Date(apiKey.createdAt).toLocaleDateString()}
                        </div>
                      </div>

                      <div>
                        <div className="text-sm font-medium">Permissions</div>
                        <div className="flex flex-wrap gap-1 mt-1">
                          {apiKey.scopes.map((scope) => (
                            <Badge key={scope} variant="outline" className="text-xs">
                              {scope}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="usage" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle>Top Endpoints</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {usageMetrics.topEndpoints.map((endpoint, index) => (
                    <div key={index} className="flex items-center justify-between">
                      <span className="font-mono text-sm">{endpoint.endpoint}</span>
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-muted-foreground">
                          {endpoint.requests.toLocaleString()}
                        </span>
                        <div className="w-20 bg-muted rounded-full h-2">
                          <div 
                            className="bg-primary h-2 rounded-full" 
                            style={{ 
                              width: `${(endpoint.requests / Math.max(...usageMetrics.topEndpoints.map(e => e.requests))) * 100}%` 
                            }}
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>24-Hour Usage Pattern</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-64 flex items-center justify-center bg-muted/20 rounded-lg">
                  <div className="text-center">
                    <BarChart3 className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                    <p className="text-muted-foreground">Hourly usage chart</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="billing" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Usage-Based Billing Configuration</CardTitle>
              <p className="text-sm text-muted-foreground">
                Configure pricing tiers and billing rules for API usage
              </p>
            </CardHeader>
            <CardContent>
              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div className="p-4 border rounded-lg">
                    <div className="text-lg font-semibold">Free Tier</div>
                    <div className="text-2xl font-bold">$0</div>
                    <div className="text-sm text-muted-foreground">Up to 10K requests</div>
                    <ul className="mt-3 space-y-1 text-sm">
                      <li>• Basic endpoints</li>
                      <li>• Standard rate limits</li>
                      <li>• Community support</li>
                    </ul>
                  </div>

                  <div className="p-4 border rounded-lg border-primary">
                    <div className="text-lg font-semibold">Premium Tier</div>
                    <div className="text-2xl font-bold">$0.001<span className="text-sm">/request</span></div>
                    <div className="text-sm text-muted-foreground">After 10K requests</div>
                    <ul className="mt-3 space-y-1 text-sm">
                      <li>• All endpoints</li>
                      <li>• Higher rate limits</li>
                      <li>• Priority support</li>
                    </ul>
                  </div>

                  <div className="p-4 border rounded-lg">
                    <div className="text-lg font-semibold">Enterprise</div>
                    <div className="text-2xl font-bold">Custom</div>
                    <div className="text-sm text-muted-foreground">Volume pricing</div>
                    <ul className="mt-3 space-y-1 text-sm">
                      <li>• Dedicated support</li>
                      <li>• Custom rate limits</li>
                      <li>• SLA guarantees</li>
                    </ul>
                  </div>
                </div>

                <div>
                  <h4 className="font-semibold mb-3">Current Month Projection</h4>
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div className="text-center p-3 border rounded">
                      <div className="text-lg font-bold">261K</div>
                      <div className="text-sm text-muted-foreground">Total Requests</div>
                    </div>
                    <div className="text-center p-3 border rounded">
                      <div className="text-lg font-bold">$251</div>
                      <div className="text-sm text-muted-foreground">Billable Usage</div>
                    </div>
                    <div className="text-center p-3 border rounded">
                      <div className="text-lg font-bold">+18%</div>
                      <div className="text-sm text-muted-foreground">vs Last Month</div>
                    </div>
                    <div className="text-center p-3 border rounded">
                      <div className="text-lg font-bold">$290</div>
                      <div className="text-sm text-muted-foreground">Projected Total</div>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="security" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Shield className="h-5 w-5" />
                  Rate Limiting
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-semibold">Global Rate Limiting</div>
                    <p className="text-sm text-muted-foreground">
                      Apply rate limits across all API keys
                    </p>
                  </div>
                  <Switch defaultChecked />
                </div>
                
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-semibold">IP-based Limiting</div>
                    <p className="text-sm text-muted-foreground">
                      Rate limit by client IP address
                    </p>
                  </div>
                  <Switch defaultChecked />
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-semibold">Burst Protection</div>
                    <p className="text-sm text-muted-foreground">
                      Prevent sudden traffic spikes
                    </p>
                  </div>
                  <Switch defaultChecked />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <AlertTriangle className="h-5 w-5" />
                  Security Monitoring
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-semibold">Anomaly Detection</div>
                    <p className="text-sm text-muted-foreground">
                      Detect unusual usage patterns
                    </p>
                  </div>
                  <Switch defaultChecked />
                </div>
                
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-semibold">Audit Logging</div>
                    <p className="text-sm text-muted-foreground">
                      Log all API access attempts
                    </p>
                  </div>
                  <Switch defaultChecked />
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-semibold">Real-time Alerts</div>
                    <p className="text-sm text-muted-foreground">
                      Notify on security events
                    </p>
                  </div>
                  <Switch defaultChecked />
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};